import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  CHECK_SCHRITTE,
  type CheckAntworten,
  type CheckFrage,
  type CheckObjekt,
  checkZusammenfassung,
  gebaeudeCheckAuswerten,
  mehrfachUmschalten,
  offeneFragen,
  sichtbareFragen,
  vorbelegung,
} from '@nkp/shared';
import { Wizard } from '../components/Wizard';
import type { WizardSchritt } from '../components/Wizard';
import { SuccessAnimation } from '../components/SuccessAnimation';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, Kennzahl } from '../components/ui/Card';
import { Select } from '../components/ui/Input';
import { IconHinweis } from '../components/icons';
import { DEMO, api, ApiFehler } from '../lib/api';
import { DEMO_OBJEKTE } from '../lib/demodaten';
import type { ApiObjektRef } from '../lib/anlagenbuch-daten';
import { cn } from '../lib/cn';
import { federIndikator, transition } from '../lib/motion';

/**
 * Gebäude-Check: geführte Erfassung des Anlagenbestands.
 *
 * Fragen und Auswertung stammen aus dem Rechenkern (`@nkp/shared`), damit Vorschau und
 * Server exakt dasselbe Ergebnis liefern. Jede gestellte Frage muss beantwortet werden;
 * „Nicht bekannt" ist eine gültige Antwort und erzeugt einen Klärungspunkt.
 */

const VERANTWORTLICH_TEXT: Record<string, string> = {
  eigentuemer: 'Eigentümer',
  verwalter: 'Verwaltung',
  mieter: 'Mieter',
};

/** Demo ohne Backend: Merkmale aus den Beispieldaten. */
function demoCheckObjekt(id: string): CheckObjekt {
  const o = DEMO_OBJEKTE.find((x) => x.id === id);
  return { baujahr: null, bundesland: 'NW', wohneinheiten: o?.einheiten ?? 3, gewerbeeinheiten: 0 };
}

interface ServerErgebnis {
  anlagen: Array<{ id: string; bezeichnung: string; pflichten: number }>;
  pflichten: number;
  vorsorglich: number;
  klaerungen: number;
}

export function GebaeudeCheck({
  objekte,
  onFertig,
  onAbbruch,
}: {
  objekte: ApiObjektRef[];
  onFertig: () => void;
  onAbbruch: () => void;
}) {
  const [objektId, setObjektId] = useState(objekte[0]?.id ?? '');
  const [merkmale, setMerkmale] = useState<CheckObjekt | null>(null);
  const [bereitsAm, setBereitsAm] = useState<string | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [antworten, setAntworten] = useState<CheckAntworten>({});
  const [sendet, setSendet] = useState(false);
  const [sendeFehler, setSendeFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState<ServerErgebnis | null>(null);
  const gesendet = useRef(false);

  // Merkmale des gewählten Objekts laden und Antworten daraus vorbelegen.
  useEffect(() => {
    if (!objektId) return;
    let aktiv = true;
    setMerkmale(null);
    setLadeFehler(null);
    setBereitsAm(null);
    const laden = DEMO
      ? Promise.resolve({ objekt: demoCheckObjekt(objektId), durchgefuehrtAm: null })
      : api.get<{ objekt: CheckObjekt; durchgefuehrtAm: string | null }>(`/objekte/${objektId}/gebaeudecheck`);
    laden
      .then((r) => {
        if (!aktiv) return;
        setMerkmale(r.objekt);
        setBereitsAm(r.durchgefuehrtAm);
        setAntworten(vorbelegung(r.objekt));
      })
      .catch((e) => {
        if (aktiv) setLadeFehler(e instanceof ApiFehler ? e.message : 'Das Objekt konnte nicht geladen werden.');
      });
    return () => {
      aktiv = false;
    };
  }, [objektId]);

  const ergebnis = useMemo(
    () => (merkmale ? gebaeudeCheckAuswerten(antworten, merkmale) : null),
    [antworten, merkmale],
  );

  function setzen(frage: CheckFrage, optionId: string) {
    setAntworten((a) => ({
      ...a,
      [frage.id]: frage.art === 'mehrfach' ? mehrfachUmschalten(a[frage.id] as string[] | undefined, optionId) : optionId,
    }));
  }

  async function uebernehmen() {
    if (gesendet.current || !merkmale || !ergebnis) return;
    gesendet.current = true;
    setSendet(true);
    setSendeFehler(null);
    try {
      if (DEMO) {
        const z = checkZusammenfassung(ergebnis);
        setFertig({
          anlagen: ergebnis.anlagen.map((x) => ({ id: x.schluessel, bezeichnung: x.bezeichnung, pflichten: x.pflichten.length })),
          pflichten: z.pflichten,
          vorsorglich: z.vorsorglich,
          klaerungen: z.klaerungen,
        });
        return;
      }
      const antwort = await api.post<ServerErgebnis>(`/objekte/${objektId}/gebaeudecheck`, { antworten });
      setFertig(antwort);
    } catch (e) {
      gesendet.current = false;
      setSendeFehler(e instanceof ApiFehler ? e.message : 'Der Gebäude-Check konnte nicht gespeichert werden.');
    } finally {
      setSendet(false);
    }
  }

  if (fertig) {
    return (
      <Card className="mb-4">
        <SuccessAnimation
          titel="Anlagenbestand erfasst"
          beschreibung={
            <>
              {fertig.anlagen.length} Anlagen mit {fertig.pflichten} Prüf- und Wartungspflichten angelegt
              {fertig.klaerungen > 0 ? `, ${fertig.klaerungen} Klärungspunkte als Maßnahmen hinterlegt` : ''}.
              {DEMO ? ' In der Demo werden keine Daten gespeichert.' : ''}
            </>
          }
          aktion={<Button onClick={onFertig}>Zum Anlagenbuch</Button>}
        />
      </Card>
    );
  }

  const objektSchritt: WizardSchritt = {
    id: 'objekt',
    titel: 'Objekt',
    hinweis:
      'Der Gebäude-Check erfasst den Anlagenbestand eines Objekts und ordnet die Prüf- und ' +
      'Wartungspflichten zu. Dauer: etwa fünf Minuten. Unbekannte Angaben werden als ' +
      'Klärungspunkt hinterlegt.',
    inhalt: (
      <div className="space-y-4">
        <Select label="Objekt" value={objektId} onChange={(e) => setObjektId(e.target.value)}>
          {objekte.map((o) => (
            <option key={o.id} value={o.id}>
              {o.bezeichnung}
            </option>
          ))}
        </Select>
        {ladeFehler && <p className="text-sm font-medium text-danger">{ladeFehler}</p>}
        {merkmale && (
          <dl className="grid grid-cols-2 gap-3 rounded-lg bg-surface-2 p-3 text-sm sm:grid-cols-4">
            <Merkmal name="Wohneinheiten" wert={String(merkmale.wohneinheiten)} />
            <Merkmal name="Gewerbeeinheiten" wert={String(merkmale.gewerbeeinheiten)} />
            <Merkmal name="Baujahr" wert={merkmale.baujahr ? String(merkmale.baujahr) : 'nicht hinterlegt'} />
            <Merkmal name="Bundesland" wert={merkmale.bundesland ?? 'nicht hinterlegt'} />
          </dl>
        )}
        {bereitsAm && (
          <p className="flex items-start gap-2 rounded-lg bg-warning-soft px-3 py-2 text-sm text-warning-soft-fg">
            <span className="mt-0.5 shrink-0">
              <IconHinweis groesse={16} />
            </span>
            Für dieses Objekt wurde der Gebäude-Check bereits durchgeführt. Weitere Anlagen bitte
            einzeln im Anlagenbuch erfassen.
          </p>
        )}
      </div>
    ),
    pruefen: () => {
      if (!merkmale) return ['Das Objekt wird noch geladen.'];
      if (bereitsAm) return ['Der Gebäude-Check wurde für dieses Objekt bereits durchgeführt.'];
      return [];
    },
  };

  const frageSchritte: WizardSchritt[] = CHECK_SCHRITTE.map((s) => ({
    id: s.id,
    titel: s.titel,
    hinweis: s.beschreibung,
    inhalt: merkmale ? (
      <div className="space-y-6">
        {sichtbareFragen(s.id, antworten, merkmale).map((f) => (
          <FrageBlock key={f.id} frage={f} wert={antworten[f.id]} onWahl={(id) => setzen(f, id)} />
        ))}
      </div>
    ) : null,
    pruefen: () =>
      merkmale ? offeneFragen(s.id, antworten, merkmale).map((f) => `Bitte beantworten: ${f.titel}`) : [],
  }));

  const zusammenfassung: WizardSchritt = {
    id: 'zusammenfassung',
    titel: 'Zusammenfassung',
    hinweis: 'Diese Anlagen und Pflichten werden angelegt. Termine der letzten Prüfungen lassen sich danach je Pflicht nachtragen.',
    inhalt: ergebnis ? (
      <Zusammenfassung ergebnis={ergebnis} sendet={sendet} fehler={sendeFehler} />
    ) : null,
  };

  const schritte = [...(objekte.length > 0 ? [objektSchritt] : []), ...frageSchritte, zusammenfassung];

  return (
    <div className="mb-4">
      <Wizard
        schritte={schritte}
        abschlussLabel={sendet ? 'Wird übernommen …' : 'Anlagen übernehmen'}
        onAbschluss={() => void uebernehmen()}
        onAbbruch={onAbbruch}
        abbruchLabel="Abbrechen"
      />
    </div>
  );
}

function Merkmal({ name, wert }: { name: string; wert: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-fg-muted">{name}</dt>
      <dd className="truncate font-medium text-fg">{wert}</dd>
    </div>
  );
}

/* ------------------------------------------------------------------ Frage */

function FrageBlock({
  frage,
  wert,
  onWahl,
}: {
  frage: CheckFrage;
  wert: string | string[] | undefined;
  onWahl: (id: string) => void;
}) {
  const mehrfach = frage.art === 'mehrfach';
  const gewaehlt = (id: string) => (Array.isArray(wert) ? wert.includes(id) : wert === id);
  return (
    <fieldset className="min-w-0">
      <legend className="text-sm font-semibold text-fg">{frage.titel}</legend>
      {frage.erlaeuterung && <p className="mt-1 text-sm leading-relaxed text-fg-muted">{frage.erlaeuterung}</p>}
      <div
        role={mehrfach ? 'group' : 'radiogroup'}
        aria-label={frage.titel}
        className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2"
      >
        {frage.optionen.map((o) => (
          <OptionKachel
            key={o.id}
            label={o.label}
            mehrfach={mehrfach}
            aktiv={gewaehlt(o.id)}
            nebensaechlich={o.id === 'unbekannt' || o.id === 'keine'}
            onClick={() => onWahl(o.id)}
          />
        ))}
      </div>
    </fieldset>
  );
}

function OptionKachel({
  label,
  mehrfach,
  aktiv,
  nebensaechlich,
  onClick,
}: {
  label: string;
  mehrfach: boolean;
  aktiv: boolean;
  nebensaechlich: boolean;
  onClick: () => void;
}) {
  const reduziert = useReducedMotion();
  return (
    <button
      type="button"
      role={mehrfach ? 'checkbox' : 'radio'}
      aria-checked={aktiv}
      onClick={onClick}
      className={cn(
        'flex min-h-[2.75rem] min-w-0 items-center gap-3 rounded-lg border px-3 py-2.5 text-left text-sm',
        'transition-colors duration-fast ease-out active:scale-[0.99]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
        aktiv
          ? 'border-accent bg-accent-soft text-accent-soft-fg'
          : cn('border-border bg-surface text-fg hover:bg-surface-2', nebensaechlich && 'text-fg-muted'),
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'relative flex h-[1.125rem] w-[1.125rem] shrink-0 items-center justify-center border',
          mehrfach ? 'rounded-sm' : 'rounded-pill',
          aktiv ? 'border-accent' : 'border-border',
        )}
      >
        {aktiv && (
          <motion.span
            layout
            initial={reduziert ? false : { scale: 0.4, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={transition(!!reduziert, federIndikator)}
            className={cn('block bg-accent', mehrfach ? 'h-2.5 w-2.5 rounded-[2px]' : 'h-2.5 w-2.5 rounded-pill')}
          />
        )}
      </span>
      <span className="min-w-0 break-words">{label}</span>
    </button>
  );
}

/* ------------------------------------------------------------------ Zusammenfassung */

function Zusammenfassung({
  ergebnis,
  sendet,
  fehler,
}: {
  ergebnis: ReturnType<typeof gebaeudeCheckAuswerten>;
  sendet: boolean;
  fehler: string | null;
}) {
  const z = checkZusammenfassung(ergebnis);
  return (
    <div className="space-y-5" aria-busy={sendet}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kennzahl bezeichnung="Anlagen" wert={z.anlagen} />
        <Kennzahl bezeichnung="Pflichten" wert={z.pflichten} />
        <Kennzahl bezeichnung="Davon vorsorglich" wert={z.vorsorglich} ton={z.vorsorglich > 0 ? 'warnung' : 'neutral'} />
        <Kennzahl bezeichnung="Klärungspunkte" wert={z.klaerungen} ton={z.klaerungen > 0 ? 'warnung' : 'neutral'} />
      </div>

      {ergebnis.anlagen.length > 0 && (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {ergebnis.anlagen.map((a) => (
            <li key={a.schluessel} className="px-3 py-3">
              <p className="text-sm font-semibold text-fg">{a.bezeichnung}</p>
              {a.pflichten.length === 0 ? (
                <p className="mt-1 text-xs text-fg-muted">Keine Prüfpflicht zugeordnet.</p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {a.pflichten.map((p) => (
                    <li key={p.katalogCode} className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm text-fg">{p.bezeichnung}</span>
                        {p.vorsorglich && (
                          <Badge ton="warnung" punkt>
                            Vorsorglich
                          </Badge>
                        )}
                        {p.verantwortlich !== 'eigentuemer' && (
                          <Badge ton="neutral">Zuständig: {VERANTWORTLICH_TEXT[p.verantwortlich]}</Badge>
                        )}
                      </div>
                      <p className="mt-0.5 text-xs text-fg-subtle">{p.grund}</p>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}

      {ergebnis.klaerungen.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-fg">Klärungspunkte</h3>
          <p className="mt-0.5 text-xs text-fg-muted">
            Werden als Maßnahmen mit einer Frist von 30 Tagen angelegt und können direkt an einen
            Fachbetrieb übergeben werden.
          </p>
          <ul className="mt-2 space-y-2">
            {ergebnis.klaerungen.map((k) => (
              <li key={k.titel} className="rounded-lg bg-warning-soft px-3 py-2">
                <p className="text-sm font-medium text-warning-soft-fg">{k.titel}</p>
                <p className="mt-0.5 text-xs text-warning-soft-fg">{k.beschreibung}</p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {ergebnis.hinweise.length > 0 && (
        <ul className="space-y-1.5">
          {ergebnis.hinweise.map((h) => (
            <li key={h} className="flex items-start gap-2 text-xs leading-relaxed text-fg-muted">
              <span className="mt-0.5 shrink-0 text-accent">
                <IconHinweis groesse={14} />
              </span>
              {h}
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-fg-subtle">
        Die Zuordnung ist eine Vorbelegung aus dem Prüfkatalog und jederzeit änderbar; sie ersetzt
        keine Rechtsberatung.
      </p>

      {fehler && <p className="text-sm font-medium text-danger">{fehler}</p>}
    </div>
  );
}

export default GebaeudeCheck;
